import type { NumberFormatter } from "../chart-interfaces";
import { DateFilter, languageMapping } from "../options";
import type { SeriesOptions } from "../types";
import { locale } from "../../translations/i18n";
import { translate } from "../utils";

// Get default language from the cookies and set it as the default locale
const languageKey = document.cookie.split('; ').find(row => row.startsWith('visitory_language'))?.split('=')[1];
const DEFAULT_LOCALE = languageKey && languageMapping[languageKey] ? languageMapping[languageKey] : 'en-US';

const getPrefix = (value: number, isChange: boolean): string =>
  value > 0 && isChange ? "+" : "";

const getDecimals = (value: number): number =>
  Math.abs(value) < 0.1
    ? 3
    : Math.abs(value) < 1
    ? 2
    : Math.abs(value) < 100
    ? 1
    : 0;

const round = (value: number, decimals: number): number =>
  Math.round(value * 10 ** decimals) / 10 ** decimals;

/**
 * Function the chart can use to format numbers
 *
 * @param value The value to format
 * @param formatToPercent If true, format as percent
 * @param locale The locale to use
 * @returns Formatted number
 */
export const numberFormatter: NumberFormatter = (
  value: number | null,
  formatToPercent: boolean = false,
  locale: string = DEFAULT_LOCALE
): string => {
  if (value === null) return "";
  formatToPercent && (value *= 100);
  const decimals = getDecimals(value);
  const roundedValue = round(value, decimals);
  const formattedValue = roundedValue.toLocaleString(locale);
  return formatToPercent ? formattedValue + " %" : formattedValue;
};

/**
 * Create label for a value
 *
 * @param value The value
 * @param unit Value's unit
 * @param isChange True if value is change
 * @param isShare True if value is share
 * @param locale The locale to use
 * @returns
 */
export const createValueLabel = (
  value: number | null,
  unit: string,
  isChange: boolean,
  isShare: boolean,
  format: string | null = null,
  locale: string = DEFAULT_LOCALE
): string => {
  if (value === null) return "";
  const prefix = getPrefix(value, isChange);
  let formattedValue = numberFormatter(value, isChange || isShare, locale);
  if (format) {
    formattedValue = autoFormat(formattedValue, format);
  }
  const suffix = isChange || isShare ? "" : " " + unit;

  return prefix + formattedValue + suffix;
};

/**
 * Create label for a date
 *
 * @param date The date
 * @param dateFilter UI Date filter
 * @param locale The locale to use
 * @returns
 */
export const createDateLabel = (
  date: Date,
  dateFilter: DateFilter,
  locale: string = DEFAULT_LOCALE
): string => {
  const options: Intl.DateTimeFormatOptions =
    dateFilter == DateFilter.YEARS
      ? { year: "numeric" }
      : { month: "short", year: "numeric" };

  let dateString = date.toLocaleDateString(locale, options);
  
  // Set first letter to uppercase
  dateString = dateString.charAt(0).toUpperCase() + dateString.slice(1);

  return dateString;
};

/**
 * Create name for a time series
 *
 * @param seriesOptions The options used to create the series name
 * @returns name
 */
export const getTimeSeriesName = (seriesOptions: SeriesOptions, verboseName?: any): string => {
  let currentLanguage: any = 'en-US';
    locale.subscribe(value => {
      currentLanguage = value.split("-")[0];
    });
  let legend = '';
  for (let key in seriesOptions.verbose_options) {
    legend = legend.concat(key + ': ' + seriesOptions.verbose_options[key][currentLanguage] + ', ');
  }

  return (
    verboseName +
    " (" +
    legend
      .replace(/["{}]/g, "")
      .replace(/,\s*$/, "") +
    ")"
  );
};


/**
 * Function for formatting the value based on the format
 *
 * @param value - input value
 * @param format - format to be applied
 * @returns formatted value
 */
export const autoFormat = (value: any, format: string): any => {
  /**
    abs:        xx xxx              if  < 1 000 000
                x,x million         if >= 1 000 000
                x,x billion         if >= 1 000 000 000
    absshort:   xx xxx              if  < 1 000 000
                x,x mill.           if >= 1 000 000
                x,x bill.           if >= 1 000 000 000
    absround:   xx xxx/x
  */
    
  let formattedValue = value;
  // replace empty space and commas
  value = Number(value.toString().replace(/\s|,/g, ''));
  switch (format) {
    case "abs":
      if (value < 1000000) {
        return formattedValue;
      } else if (value < 1000000000) {
        formattedValue = (value / 1000000).toFixed(1) + " " + translate("million");
      } else {
        formattedValue = (value / 1000000000).toFixed(1) + " " + translate("billion");
      }
      break;

    case "absshort":
      if (value < 1000000) {
        return formattedValue;
      } else if (value < 1000000000) {
        formattedValue = (value / 1000000).toFixed(1) + " " + translate("mill") + ".";
      } else {
        formattedValue = (value / 1000000000).toFixed(1) + " " + translate("bill") + ".";
      }
      break;

    case "absround":
      formattedValue = Math.round(value);
      break;

    default:
      formattedValue = value;
  }

  return formattedValue;
}

